import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './FullPageScroll.module.css';

const THROTTLE_MS = 400;
const ANIM_DURATION = 650;

const FullPageScroll = ({ children, sectionThemes = [], sectionLabels = [] }) => {
  const sections = React.Children.toArray(children);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animDir, setAnimDir] = useState('down');
  const lastScrollTime = useRef(0);
  const touchStartY = useRef(null);
  const sectionRefs = useRef([]);

  const currentTheme = sectionThemes[currentIndex] || 'light';
  const isDark = currentTheme === 'dark';

  const goTo = useCallback((index) => {
    if (isTransitioning || index === currentIndex) return;
    if (index < 0 || index >= sections.length) return;
    const dir = index > currentIndex ? 'down' : 'up';
    setAnimDir(dir);
    setIsTransitioning(true);
    setCurrentIndex(index);
    lastScrollTime.current = Date.now();
    window.dispatchEvent(new CustomEvent('fp:change', { detail: { index, theme: sectionThemes[index] || 'light' } }));
    setTimeout(() => setIsTransitioning(false), ANIM_DURATION);
  }, [currentIndex, isTransitioning, sections.length, sectionThemes]);

  const navigate = useCallback((dir) => {
    const now = Date.now();
    if (now - lastScrollTime.current < THROTTLE_MS) return;
    if (isTransitioning) return;

    const target = currentIndex + (dir === 'down' ? 1 : -1);
    if (target < 0 || target >= sections.length) return;

    const sectionEl = sectionRefs.current[currentIndex];
    if (sectionEl) {
      const { scrollTop, scrollHeight, clientHeight } = sectionEl;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 3;
      const atTop = scrollTop <= 3;
      if (dir === 'down' && !atBottom) return;
      if (dir === 'up' && !atTop) return;
    }

    lastScrollTime.current = now;
    setAnimDir(dir);
    setIsTransitioning(true);
    setCurrentIndex(target);
    window.dispatchEvent(new CustomEvent('fp:change', { detail: { index: target, theme: sectionThemes[target] || 'light' } }));
    setTimeout(() => setIsTransitioning(false), ANIM_DURATION);
  }, [currentIndex, isTransitioning, sections.length, sectionThemes]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Wheel
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); navigate(e.deltaY > 0 ? 'down' : 'up'); };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [navigate]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); navigate('down'); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); navigate('up'); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Touch
  useEffect(() => {
    const onStart = (e) => { touchStartY.current = e.touches[0].clientY; };
    const onEnd = (e) => {
      if (touchStartY.current === null) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) navigate(diff > 0 ? 'down' : 'up');
      touchStartY.current = null;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [navigate]);

  // Hash-based anchor navigation (for navbar links like #about, #services)
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const id = anchor.getAttribute('href').slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      sectionRefs.current.forEach((sectionEl, i) => {
        if (sectionEl && sectionEl.contains(el)) goTo(i);
      });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [goTo]);

  const progress = sections.length > 1 ? (currentIndex / (sections.length - 1)) * 100 : 100;

  const navUI = ReactDOM.createPortal(
    <>
      <div className={styles.progressBar} aria-hidden="true">
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>
      <nav
        className={`${styles.dotNav} ${isDark ? styles.navDark : ''}`}
        aria-label="Page navigation"
      >
        {sections.map((_, i) => (
          <button
            key={i}
            className={`${styles.dot} ${i === currentIndex ? styles.dotActive : ''}`}
            onClick={() => goTo(i)}
            aria-label={sectionLabels[i] ? `Go to ${sectionLabels[i]}` : `Section ${i + 1}`}
            title={sectionLabels[i] || `Section ${i + 1}`}
          />
        ))}
      </nav>
      <div
        className={`${styles.counter} ${isDark ? styles.counterDark : ''}`}
        aria-live="polite"
        aria-atomic="true"
      >
        <span className={styles.counterCurrent}>{String(currentIndex + 1).padStart(2, '0')}</span>
        <span className={styles.counterSep}> / </span>
        <span>{String(sections.length).padStart(2, '0')}</span>
      </div>
    </>,
    document.body
  );

  return (
    <>
      <div className={styles.container}>
        {sections.map((section, i) => {
          const state = i === currentIndex ? 'active' : i < currentIndex ? 'above' : 'below';
          return (
            <div
              key={i}
              ref={el => { sectionRefs.current[i] = el; }}
              className={styles.section}
              data-state={state}
              data-dir={animDir}
              aria-hidden={i !== currentIndex}
            >
              {section}
            </div>
          );
        })}
      </div>
      {navUI}
    </>
  );
};

export default FullPageScroll;
